import { PropsWithChildren, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Stack } from '@mui/material';

import LabeledTextField from '../LabeledTextField';
import { LabeledTextFieldProps } from '../LabeledTextField/LabeledTextField';

import ConfirmDialog, { ConfirmDialogProps } from './ConfirmDialog';

interface ConfirmDialogWithInputProps extends Omit<ConfirmDialogProps, 'onConfirm'> {
  inputProps: LabeledTextFieldProps;
  onConfirm: (value: string) => void;
}

interface IFormInput {
  input: string;
}

export default function ConfirmDialogWithInput({
  children,
  inputProps,
  onConfirm,
  ...props
}: PropsWithChildren<ConfirmDialogWithInputProps>) {
  const formRef = useRef<HTMLFormElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) => onConfirm(data.input);

  const handleConfirm = () => {
    formRef.current?.dispatchEvent(
      new SubmitEvent('submit', {
        bubbles: true,
        cancelable: true,
      }),
    );
    return false;
  };

  return (
    <ConfirmDialog {...props} onConfirm={handleConfirm}>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Stack spacing={2}>
          <div>{children}</div>
          <LabeledTextField
            {...inputProps}
            InputProps={{
              ...register('input', { required: true, minLength: 1 }),
            }}
            error={!!errors.input}
            helperText={errors.input?.type === 'required' ? 'This field is required' : ''}
            autoFocus
            fullWidth
            required
          />
        </Stack>
      </form>
    </ConfirmDialog>
  );
}
